import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getnetLogin, getnetPagar } from "../../../services/backend/getnetService";
import { pagarCredito } from "../../../services/backend/cotizacionService";
import logger from "../../../shared/utils/logger";
import { StorageKeys, session } from "../../../utils/storage";

const usePayment = ({ total, cotizacionId, onValidateBuyer, onShowToast }) => {
  const navigate = useNavigate();
  const [paymentLoading, setPaymentLoading] = useState("");
  const [paymentError, setPaymentError] = useState("");

  const isPaymentBusy = Boolean(paymentLoading);
  const isPaymentReady = Number(total) > 0;
  const safeTotal = Number(total) || 0;

  const validateBeforePayment = useCallback(async () => {
    if (!isPaymentReady) return false;
    if (!(await onValidateBuyer("Revisa los campos del comprador antes de continuar."))) return false;
    return true;
  }, [isPaymentReady, onValidateBuyer]);

  const handlePaymentError = useCallback((error, contextMessage, fallbackMessage) => {
    logger.error(contextMessage, error);
    const message = error?.message || fallbackMessage;
    setPaymentError(message);
    onShowToast?.({ severity: "error", summary: "Error", detail: message, life: 4000 });
  }, [onShowToast]);

  const handleTransactionRequest = useCallback(async () => {
    if (!(await validateBeforePayment())) return;

    setPaymentLoading("transaction");
    setPaymentError("");
    try {
      if (!String(cotizacionId || "").trim())
        throw new Error("No se encontró una cotización activa.");
      await getnetLogin();
      const response = await getnetPagar({
        cancelar: `${window.location.origin}/checkout/rechazado`,
        monto: Math.max(0, Math.round(safeTotal)),
        referencia: String(cotizacionId),
        retorno: `${window.location.origin}/checkout/procesando`,
      });
      const { processUrl, key } = response || {};
      if (!processUrl)
        throw new Error("No se recibió URL de redirección desde el backend.");

      session.set(StorageKeys.GETNET_KEY, key);
      session.set(StorageKeys.GETNET_PEDIDO, String(cotizacionId));

      onShowToast?.({
        severity: "success",
        summary: "Pago iniciado",
        detail: "Redirigiendo al portal de pago...",
        life: 2200,
      });
      window.location.assign(processUrl);
    } catch (error) {
      handlePaymentError(error, "No fue posible ejecutar el pago.", "No fue posible ejecutar el pago.");
    } finally {
      setPaymentLoading("");
    }
  }, [validateBeforePayment, safeTotal, cotizacionId, onShowToast, handlePaymentError]);

  const handleCreditoRequest = useCallback(async () => {
    if (!(await validateBeforePayment())) return;

    setPaymentLoading("credito");
    setPaymentError("");
    try {
      const id = String(cotizacionId || "").trim();
      if (!id) throw new Error("No se encontró una cotización activa.");

      await pagarCredito(id);

      onShowToast?.({
        severity: "success",
        summary: "Pago a crédito",
        detail: "Pago a crédito registrado correctamente.",
        life: 3000,
      });
      navigate("/checkout/exito", { replace: true });
    } catch (error) {
      handlePaymentError(error, "No fue posible registrar el pago a crédito.", "No fue posible registrar el pago a crédito.");
    } finally {
      setPaymentLoading("");
    }
  }, [validateBeforePayment, cotizacionId, onShowToast, navigate, handlePaymentError]);

  return {
    paymentLoading,
    paymentError,
    isPaymentBusy,
    isPaymentReady,
    handleTransactionRequest,
    handleCreditoRequest,
  };
};

export default usePayment;